// App admin support mode. One browser tab acts inside one company's ABX data.
// The session lives in sessionStorage, so other tabs keep the admin's own company.
// The server stays authoritative: it rejects the header from users who are not app admins.

const STORAGE_KEY = 'abxSupportSession';

export const ACTING_COMPANY_HEADER = 'X-Acting-Company-Id';

// Must match the server allowlist in ProgramSupportContext.ts.
const SUPPORTED_PATH =
    /^\/(?:sites\/\d+\/(?:residents|surveillance-cases|programs)(?:\/|$)|programs\/abx-stewardship\/(?:sites|sites-overview|site-comparison)\/?$)/i;
const UNSUPPORTED_PATH =
    /^\/sites\/\d+\/programs\/[^/]+\/learndash-courses(?:\/|$)/i;

const storage = () =>
    typeof sessionStorage === 'undefined' ? null : sessionStorage;

const positiveId = (value) => {
    const id = Number(value);
    return Number.isSafeInteger(id) && id > 0 ? id : null;
};

export function readSupportSession() {
    try {
        const raw = storage()?.getItem(STORAGE_KEY);
        if (!raw) return null;
        const session = JSON.parse(raw);
        const companyId = positiveId(session?.companyId);
        if (!companyId) return null;
        // The site is optional. Without one, the Program page asks the admin to choose a site.
        const siteId = positiveId(session?.siteId);
        return {
            companyId,
            siteId,
            companyName: String(session.companyName || ''),
            companyHash: String(session.companyHash || ''),
        };
    } catch {
        return null;
    }
}

export function writeSupportSession({
    companyId,
    siteId,
    companyName,
    companyHash,
}) {
    const session = {
        companyId: positiveId(companyId),
        siteId: positiveId(siteId),
        companyName: companyName || '',
        companyHash: companyHash || '',
    };
    if (!session.companyId) {
        throw new TypeError('Support mode needs a company.');
    }
    storage()?.setItem(STORAGE_KEY, JSON.stringify(session));
    return session;
}

export function clearSupportSession() {
    storage()?.removeItem(STORAGE_KEY);
}

export function readSupportCompanyId() {
    return readSupportSession()?.companyId ?? null;
}

// Header for a v2 path such as `/sites/7/programs/abx-stewardship/abx-cases`, or null.
export function actingCompanyHeaderFor(path) {
    const companyId = readSupportCompanyId();
    if (!companyId) return null;
    const pathname = String(path || '').split('?')[0];
    if (!SUPPORTED_PATH.test(pathname) || UNSUPPORTED_PATH.test(pathname)) {
        return null;
    }
    return String(companyId);
}

// Keeps the support tab's selected site in the same session, not in shared localStorage.
// A null site clears the selection, so a reload asks for a site again.
export function updateSupportSessionSite(siteId) {
    const session = readSupportSession();
    if (!session) return;
    writeSupportSession({ ...session, siteId });
}
